import { CommonModule } from '@angular/common';
import { AfterContentInit, Component, inject, Input } from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'lib-form-field',
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './form-field.component.html',
  styleUrl: './form-field.component.scss',
  standalone: true,
})
export class FormFieldComponent implements AfterContentInit {
  /**
   * Texto do label exibido acima do campo.
   * @example "Nome Completo"
   */
  @Input() label = '';

  /**
   * Nome do controle do formulário associado a este campo.
   * Pode ser um campo direto (ex: "nome") ou aninhado (ex: "endereco.codPostal").
   * @example "cidade" | "endereco.codPostal" | "usuario.perfil.nome"
   */
  @Input() controlName = '';

  /**
   * Indica se o campo é obrigatório.
   * @default false
   */
  @Input() required = false;

  /**
   * Tamanho do campo: 'full', 'large', 'medium', 'small'
   * @default "full"
   */
  @Input() size: 'full' | 'large' | 'medium' | 'small' = 'full';

  /**
   * Texto de ajuda exibido abaixo do campo.
   * @example "Informe o nome completo do cliente"
   */
  @Input() helpText = '';

  /**
   * Ícone a ser exibido como prefixo no campo (opcional).
   * @example "pi pi-user"
   */
  @Input() iconPrefix: string | null = null;

  /**
   * Classes CSS adicionais para customização do campo.
   * @example "campo-personalizado"
   */
  @Input() className = '';

  /**
   * Se deve ocupar toda a largura disponível independente do size
   * @default false
   */
  @Input() fullWidth = false;

  /**
   * Limite máximo de caracteres para exibir contador abaixo do campo.
   * @example 100
   */
  @Input() maxLength?: number;

  controlContainer = inject(ControlContainer);
  control: AbstractControl | null = null;

  ngAfterContentInit() {
    this.control = this.findControl();
  }

  /**
   * Busca o controle do formulário, suportando campos aninhados
   * @returns AbstractControl | null
   */
  private findControl(): AbstractControl | null {
    if (!this.controlContainer?.control || !this.controlName) {
      return null;
    }

    // Tenta buscar o controle usando o caminho completo
    const foundControl = this.controlContainer.control.get(this.controlName);

    if (foundControl) {
      return foundControl;
    }

    // Se não encontrou, tenta buscar por partes (para campos aninhados)
    return this.findNestedControl(
      this.controlContainer.control,
      this.controlName
    );
  }

  /**
   * Busca recursivamente por controles aninhados
   * @param parentControl - Controle pai
   * @param path - Caminho do controle (ex: "endereco.codPostal")
   * @returns AbstractControl | null
   */
  private findNestedControl(
    parentControl: AbstractControl,
    path: string
  ): AbstractControl | null {
    const parts = path.split('.');
    let currentControl: AbstractControl | null = parentControl;

    for (const part of parts) {
      if (
        currentControl &&
        'get' in currentControl &&
        typeof currentControl.get === 'function'
      ) {
        currentControl = currentControl.get(part);
        if (!currentControl) {
          return null;
        }
      } else {
        return null;
      }
    }

    return currentControl;
  }

  /**
   * Verifica se deve mostrar um erro específico
   * @param errorName - Nome do erro (ex: 'required', 'email')
   * @returns boolean
   */
  showError(errorName: string): boolean {
    if (!this.control?.touched || !this.control?.errors?.[errorName]) {
      return false;
    }

    if (errorName === 'maxlength' || errorName === 'minlength') {
      return true;
    }

    return this.required;
  }

  /**
   * Verifica se o campo tem algum erro
   * @returns boolean
   */
  get hasError(): boolean {
    if (!this.control?.touched || !this.control?.invalid) {
      return false;
    }

    if (
      this.control.errors?.['maxlength'] ||
      this.control.errors?.['minlength']
    ) {
      return true;
    }

    return this.required;
  }

  get currentLength(): number {
    const value = this.control?.value;
    if (value == null) {
      return 0;
    }

    return String(value).length;
  }

  get remainingCharacters(): number {
    if (!this.maxLength) {
      return 0;
    }

    return Math.max(0, this.maxLength - this.currentLength);
  }

  get isNearCharacterLimit(): boolean {
    if (!this.maxLength) {
      return false;
    }

    return this.currentLength / this.maxLength >= 0.8;
  }

  /**
   * Retorna as classes CSS do campo
   * @returns string
   */
  get fieldClasses(): string {
    const baseClasses = ['n-form-field'];

    if (this.fullWidth) {
      baseClasses.push('n-field-full-width');
    } else {
      baseClasses.push(`n-field-${this.size}`);
    }

    if (this.hasError) {
      baseClasses.push('n-field-error');
    }

    if (this.className) {
      baseClasses.push(this.className);
    }

    return baseClasses.join(' ');
  }

  /**
   * Retorna o ID único para o campo (útil para acessibilidade)
   * @returns string
   */
  get fieldId(): string {
    return this.controlName.replace(/\./g, '_');
  }
}
